<script lang="ts">
	import '../app.css';
	import { onMount } from 'svelte';
	import { fly } from 'svelte/transition';
	import { useRegisterSW } from 'virtual:pwa-register/svelte';
	import { session } from '$lib/auth/session.svelte';
	import { applyLocale, i18n } from '$lib/i18n/i18n.svelte';
	import { network } from '$lib/state/network.svelte';
	import { notifications } from '$lib/state/notifications';
	import { resync } from '$lib/state/resync';
	import { applyTheme, theme } from '$lib/state/theme.svelte';
	import { toast, type ToastKind } from '$lib/state/toast.svelte';
	import { units } from '$lib/state/units.svelte';

	let { children } = $props();

	const t = $derived(i18n.t);

	/** The Spinner's marks on its status colours. */
	const TOAST_LOOKS: Record<ToastKind, { circle: string; glyph: string }> = {
		error: { circle: 'bg-error', glyph: '✕' },
		warning: { circle: 'bg-warn', glyph: '!' },
		success: { circle: 'bg-success', glyph: '✓' }
	};

	const { needRefresh, updateServiceWorker } = useRegisterSW();

	onMount(() => {
		applyTheme(theme.current);
		applyLocale(i18n.locale);
		void session.start();
	});

	$effect(() => {
		if (network.online) void resync();
	});

	// The first messages once signed in; later ones come with every resync.
	$effect(() => {
		if (session.status === 'ready') void notifications.refresh();
	});

	// The setup flow's unit picker; once in the app, the units come with every resync.
	$effect(() => {
		if (session.status === 'setup') void units.refresh();
	});

	function onvisibilitychange() {
		if (document.visibilityState === 'visible') void resync();
	}
</script>

<svelte:document {onvisibilitychange} />

{@render children()}

{#if !network.online}
	<div
		role="status"
		class="fixed inset-x-3 top-3 z-[90] mx-auto flex max-w-md items-start gap-2.5 rounded-[16px_16px_16px_5px] border-2 border-dark bg-warn px-3.5 py-2.5 text-[13px] leading-[1.35] font-bold text-dark"
	>
		<span aria-hidden="true" class="text-base leading-none">⚡</span>
		<span>{t.layout.offline}</span>
	</div>
{/if}

<!-- Above the bottom menu: the new-version pill, then the toasts, newest at the bottom. -->
<div
	class="pointer-events-none fixed inset-x-3 bottom-[88px] z-[95] mx-auto flex max-w-md flex-col gap-2 desk:bottom-6"
>
	{#if $needRefresh}
		<div
			role="status"
			class="pointer-events-auto flex items-center gap-2.5 rounded-[16px_16px_16px_5px] bg-dark py-2.5 pr-2.5 pl-3.5 text-sm font-bold text-cream shadow-[3px_3px_0_var(--primary)]"
		>
			<span class="flex-1">{t.layout.newVersion}</span>
			<button
				type="button"
				class="h-9 rounded-full border-2 border-cream bg-warn px-3.5 text-[13px] font-extrabold text-dark"
				onclick={() => updateServiceWorker(true)}
			>
				{t.layout.update}
			</button>
		</div>
	{/if}
	{#each toast.items as item (item.id)}
		<div
			role={item.kind === 'error' ? 'alert' : 'status'}
			class="pointer-events-auto flex items-center gap-3 rounded-[16px_16px_16px_5px] bg-dark px-3.5 py-3 text-sm leading-[1.35] font-bold text-cream"
			transition:fly={{ y: 16, duration: 180 }}
		>
			<span
				aria-hidden="true"
				class="grid size-[26px] flex-none place-items-center rounded-full text-sm font-extrabold text-dark {TOAST_LOOKS[
					item.kind
				].circle}"
			>
				{TOAST_LOOKS[item.kind].glyph}
			</span>
			<span class="flex-1">{item.text}</span>
			{#if item.action}
				<button
					type="button"
					class="-my-1 h-9 flex-none rounded-full border-none bg-warn px-3.5 text-[13px] font-extrabold text-dark"
					onclick={() => toast.act(item.id)}
				>
					{item.action.label}
				</button>
			{/if}
			<button
				type="button"
				aria-label={t.common.close}
				class="-my-1 size-8 flex-none rounded-full bg-transparent text-lg text-cream opacity-60"
				onclick={() => toast.dismiss(item.id)}
			>
				×
			</button>
		</div>
	{/each}
</div>
